import { BaseControl, IControlParameters } from '@Common/Controls/BaseControl';
import { Control, IControl } from '@Common/Controls/Controls';
import * as commonUtils from '@Common/Helpers/CommonUtils';
import errorHelper from '@Common/Helpers/ErrorHelper';
import StringUtils from '@Common/Helpers/StringUtils';
import urlHelper from '@Common/Helpers/UrlHelper';
import { ISpinnerSetting } from '@Common/Plugins/spinner';
import Guid from '@Common/Core/Guid';
import { memoize } from '@Common/Core/MemoizeDecorator';

export interface IGridTableDescription {
    id?: string;
    style?: any;
    columns?: IGridColumnDescription[];
    rows?: IGridRowDescription[];
    cells?: IGridCellDescription[];
    className?: string;
}

export interface IGridLayoutParameters extends IControlParameters, IGridTableDescription {
    hasSpinner?: boolean;
}

export interface IGridColumnDescription {
    style?: IGridColumnStyle;
}

interface IGridColumnStyle {
    width?: string;
    maxWidth?: string | number;
    minWidth?: string | number;
    padding?: string;
    paddingRight?: string;
    paddingLeft?: string;
    paddingBottom?: string;
    paddingTop?: string;
    textAlign?: string;
    verticalAlign?: string;
    align?: string;
    fontSize?: string;
    textOverflow?: string;
}

export interface IGridRowDescription {
    style?: IGridRowStyle;
    className?: string;
}

interface IGridRowStyle {
    height?: string;
    minHeight?: number | string;
    maxHeight?: number | string;
    display?: string;
    adjustingHeight?: boolean;
    padding?: string;
    verticalAlign?: string;
}

export interface IGridCellDescription {
    rowIndex: number;
    columnIndex: number;
    colSpan?: number;
    content?: any;
    table?: IGridTableDescription;
    style?: Partial<CSSStyleDeclaration> & Record<string, any>;
    adjustingWidth?: boolean;
    adjustingHeight?: boolean;
    adjusting?: boolean;
    className?: string;
    onClick?: (cell: any) => void;
    onMouseOver?: (cell: any) => void;
    onSizeChanged?: (width: number, height: number) => void;
    rowSpan?: number;
}

export interface IGridLayoutCell extends IGridCellDescription {
    setContent?(content: { getControl(): JQuery } | JQuery | string, skipAdjust?: boolean): void;

    getContent?<T = Content>(): T;

    setCss?(css: any): void;
}

type Dimension = 'width' | 'height';

type SizeDefinition = {
    isPercentMaxValue: boolean;
    maxValue: number;
    isPercentMinValue: boolean;
    minValue: number;
    isPercentValue: boolean;
    isAsteriskValue: boolean;
    value: number;
};
type Content = string | JQuery | IControl | Control;
type CellDescription = Required<Pick<IGridLayoutCell, Exclude<keyof IGridLayoutCell, keyof IGridCellDescription>>> &
    IGridCellDescription & {
        control: IControl;
        content: Content;
        table: TableDescriptor;
        instance: GridLayout;
        cell: JQuery;
        column: IGridColumnDescription;
        row: RowDescription;
        actualHeight: number;
        actualWidth: number;
    };

type RowDescription = IGridRowDescription & { row: JQuery };
type TableDescriptor = IGridLayoutParameters & {
    table: JQuery;
    instance: GridLayout;
    rows: RowDescription[];
    cells: CellDescription[];
};

type AdjustCallback = (control: IControl, width: number, height: number) => void;

export class GridLayout extends BaseControl {
    private readonly _autoSizeMark = '*';
    private readonly tableDescriptor: TableDescriptor;
    private _currentWidth: number;
    private _currentHeight: number;
    private adjustingCallback: AdjustCallback;

    constructor(param: IGridLayoutParameters) {
        super();
        this.tableDescriptor = _.defaults(param, {
            css: null,
            className: '',
            width: 'auto',
            height: 'auto',
            hasSpinner: false,
            table: null
        }) as any;

        const table = this.getTable();
        this.tableDescriptor.table = table;
        this.tableDescriptor.instance = this;
        table.data('instance', this);
        if (this.tableDescriptor.hasSpinner) {
            this._root = $('<div>', {
                css: { position: 'relative' },
                html: table
            });
        } else {
            this._root = table;
        }
    }

    private getTable(): JQuery {
        const desc = this.tableDescriptor;
        const table = $('<table cellspacing="0" cellpadding="0" class="grid-layout"><thead></thead><tbody></tbody></table>');
        desc.id = desc.id || Guid.newGuid();
        table.attr('id', desc.id);
        desc.style && table.css(desc.style);
        desc.className && table.addClass(desc.className);

        if (desc.width !== 'auto') {
            table.width(desc.width);
        }
        if (desc.height !== 'auto') {
            table.height(desc.height);
        }

        _(desc.rows).forEach((row, rowIndex) => {
            const tr = $('<tr />').appendTo(table);
            row.style && tr.css(this.getDefaultStyle(row.style));
            row.className && tr.addClass(row.className);
            row.row = tr;

            let skipColumn = -1;
            _(desc.columns).forEach((column, columnIndex) => {
                if (columnIndex < skipColumn) {
                    return;
                }

                const td = $('<td class="grid-layout-cell" />').appendTo(tr);
                column.style && td.css(this.getDefaultStyle(column.style));

                let cell = this.getCell(rowIndex, columnIndex) as CellDescription;
                if (!cell) {
                    cell = { rowIndex: rowIndex, columnIndex: columnIndex, content: null } as any;
                    desc.cells.push(cell);
                }
                cell.cell = td;
                cell.column = column;
                cell.row = row;
                cell.cell.on('click', () => cell.onClick && cell.onClick(cell));
                if (cell.onMouseOver) {
                    cell.cell.on('mouseover', () => cell.onMouseOver(cell));
                }

                cell.style && td.css(this.getDefaultStyle(cell.style));
                cell.className && td.addClass(cell.className);

                if (cell.colSpan) {
                    td.attr('colspan', cell.colSpan);
                    skipColumn = columnIndex + cell.colSpan;
                }

                if (cell.rowSpan) {
                    td.attr('rowspan', cell.rowSpan);
                }

                if (cell.table) {
                    const child = new GridLayout(cell.table);
                    child.getControl().appendTo(td);
                } else if (cell.content) {
                    this.setCellContent(td, cell);
                }

                cell.setContent = (content, skipAdjust = false) => {
                    cell.content instanceof $ && (cell.content as JQuery).detach();
                    cell.content = content;
                    this.setCellContent(td, cell, !skipAdjust);
                };

                cell.getContent = () => cell.control as any;

                cell.setCss = css => cell.cell.css(css as any);
            });
        });

        return table;
    }

    private setCellContent(container: JQuery, cell: CellDescription, needAdjust?: boolean): void {
        if (cell.content) {
            if (_.isString(cell.content)) {
                container.addClass('label-control').append((cell.content = $('<label />').text(cell.content)));
            } else if (cell.content instanceof jQuery) {
                container.append(cell.content as JQuery);
            } else {
                cell.control = cell.content;
                if (cell.content.getControl) {
                    cell.content = cell.content.getControl();
                    container.append(cell.content);
                }
            }

            if (container.find('> label').length) {
                container.addClass('label-control-cell');
            } else if (container.find('> .toolbar').length) {
                container.addClass('no-gap-bottom');
            }
        } else {
            cell.control = null;
        }
        needAdjust && this.adjust();
    }

    public cellContent(rowIndex: number, colIndex: number, content?: Content): Content {
        const cell = this.getCell(rowIndex, colIndex) as CellDescription;
        if (arguments.length === 3) {
            cell.setContent(content);
        }
        return cell.control || cell.content;
    }

    private getDefaultStyle(style: IGridColumnStyle & IGridRowStyle): any {
        style = commonUtils.clone(style);
        if (_.isString(style.width) && style.width.includes(this._autoSizeMark)) {
            style.width = 'auto';
        }
        if (_.isString(style.height) && style.height.includes(this._autoSizeMark)) {
            style.height = 'auto';
        }

        return style as any;
    }

    public getCell(rowIndex: number, columnIndex: number): CellDescription | undefined {
        return _.find(this.tableDescriptor.cells, cell => cell.rowIndex === rowIndex && cell.columnIndex === columnIndex);
    }

    public adjust(width?: number | any, height?: number | any): void {
        if (this.tableDescriptor.width !== 'auto') {
            width = this.tableDescriptor.width;
        }
        if (this.tableDescriptor.height !== 'auto') {
            height = this.tableDescriptor.height;
        }

        width = width == null ? this._currentWidth : width;
        height = height == null ? this._currentHeight : height;

        this._currentWidth = width;
        this._currentHeight = height;
        this.setSize(width, height);
    }

    private getAutoSize(tableDescriptor: TableDescriptor, obj: RowDescription | IGridColumnDescription, propertyName: Dimension): number {
        let result = 0;
        const outerPropertyName = propertyName === 'width' ? 'outerWidth' : 'outerHeight';
        _(tableDescriptor.cells)
            .filter(cell => !cell.colSpan || cell.colSpan === 1 || propertyName !== 'width')
            .forEach(cell => {
                if (!obj || obj === cell.column || obj === cell.row) {
                    const childElement = cell.content ? cell.content : cell.table ? cell.table.table : null;
                    if (childElement && childElement[propertyName] && childElement.css && childElement.css(propertyName).toLowerCase() !== 'auto') {
                        let paddingValue = 0;
                        let borderValue = 0;
                        if (cell.cell) {
                            paddingValue =
                                propertyName === 'width'
                                    ? parseInt(cell.cell.css('padding-left')) + parseInt(cell.cell.css('padding-right'))
                                    : parseInt(cell.cell.css('padding-top')) + parseInt(cell.cell.css('padding-bottom'));
                            borderValue =
                                propertyName === 'width'
                                    ? parseInt(cell.cell.css('border-left-width')) + parseInt(cell.cell.css('border-right-width'))
                                    : parseInt(cell.cell.css('border-top-width')) + parseInt(cell.cell.css('border-top-width'));
                        }

                        const outerValue = Math.ceil(childElement[outerPropertyName](true) + paddingValue + borderValue);
                        const minSize = childElement.css('min-' + propertyName);
                        const maxSize = childElement.css('max-' + propertyName);
                        const minSizeValue = minSize && !isNaN(parseInt(minSize)) ? parseInt(minSize) : 0;
                        const maxSizeValue = maxSize && !isNaN(parseInt(maxSize)) ? parseInt(maxSize) : Number.MAX_VALUE;
                        if (outerValue > result && outerValue >= minSizeValue && outerValue <= maxSizeValue) {
                            result = Math.ceil(outerValue);
                        }
                    }
                }
            });

        return result;
    }

    public setAdjustingCallback(adjustingCallback: AdjustCallback): void {
        this.adjustingCallback = adjustingCallback;
    }

    private setSize(width: number, height?: number): void {
        const tableDescriptor = this.tableDescriptor;
        tableDescriptor.table.css({ width: width ? width : 'auto', height: height ? height : 'auto' });
        width -= parseInt(tableDescriptor.table.css('padding-left') || '0') + parseInt(tableDescriptor.table.css('padding-right') || '0');
        if (height) {
            height -= parseInt(tableDescriptor.table.css('padding-top') || '0') + parseInt(tableDescriptor.table.css('padding-bottom') || '0');
        }

        const rowHeights = _.map(tableDescriptor.rows, row => this.getSize(tableDescriptor, row, 'height'));

        const columnWidths = _.map(tableDescriptor.columns, column => this.getSize(tableDescriptor, column, 'width'));

        const actualHeights = height ? this.getActualSizes(height, rowHeights) : [];
        const actualWidths = this.getActualSizes(width, columnWidths);

        _(tableDescriptor.rows).forEach((row, rowIndex) => {
            height && row.row.css({ height: actualHeights[rowIndex] ? actualHeights[rowIndex] : 0 });
            _(tableDescriptor.columns).forEach((column, columnIndex) => {
                const cell = this.getCell(rowIndex, columnIndex) as CellDescription;
                if (cell && cell.cell) {
                    const size = GridLayout.getColSpanCellsSize(cell, actualWidths, actualHeights);
                    cell.cell.css({
                        width: size.width ? size.width : 0,
                        height: size.height ? size.height : 'auto'
                    });
                    cell.actualWidth = actualWidths[columnIndex];
                    cell.actualHeight = height ? actualHeights[rowIndex] : 0;
                    if (cell.control && cell.control.adjust) {
                        const controlWidth =
                            size.width -
                            parseInt(cell.cell.css('padding-left') || '0') -
                            parseInt(cell.cell.css('padding-right') || '0') -
                            parseInt(cell.cell.css('border-width') || '0') * 2;
                        const controlHeight =
                            size.height -
                            parseInt(cell.cell.css('padding-top') || '0') -
                            parseInt(cell.cell.css('padding-bottom') || '0') -
                            parseInt(cell.cell.css('border-width') || '0') * 2;

                        const adjustingWidth = cell.adjustingWidth !== false ? (controlWidth && controlWidth > 0 ? controlWidth : 0) : null;
                        const adjustingHeight = cell.adjustingHeight !== false ? (controlHeight && controlHeight > 0 ? controlHeight : 0) : null;
                        if (this.adjustingCallback) {
                            this.adjustingCallback(cell.control, adjustingWidth, adjustingHeight);
                        } else if (cell.adjusting !== false) {
                            cell.control.adjust(adjustingWidth, adjustingHeight);
                        }
                    }

                    cell.onSizeChanged &&
                        cell.onSizeChanged(
                            cell.actualWidth && cell.actualWidth > 0 ? cell.actualWidth : 0,
                            cell.actualHeight && cell.actualHeight > 0 ? cell.actualHeight : 0
                        );
                }
            });
        });

        _(tableDescriptor.cells).forEach(cell => {
            if (cell.table && cell.adjusting !== false) {
                const size = GridLayout.getColSpanCellsSize(cell, actualWidths, actualHeights);

                let innerTableWidth = size.width;
                let innerTableHeight = size.height;
                if (cell.cell) {
                    let padding = parseInt(cell.cell.css('padding-left')) + parseInt(cell.cell.css('padding-right'));
                    padding = isNaN(padding) ? 0 : padding;
                    innerTableWidth -= padding;
                    padding = parseInt(cell.cell.css('padding-top')) + parseInt(cell.cell.css('padding-bottom'));
                    padding = isNaN(padding) ? 0 : padding;
                    innerTableHeight -= padding;
                }

                cell.table.instance.setSize(
                    cell.adjustingWidth !== false ? innerTableWidth : 0,
                    cell.adjustingHeight !== false ? innerTableHeight : 0
                );
            }
        });
    }

    private validate(tableDescriptor: TableDescriptor, width: number, height: number): void {
        if (width < 0) {
            width = 0;
        }
        if (height < 0) {
            height = 0;
        }
        if ((width && tableDescriptor.table.width() > width) || (height && tableDescriptor.table.height() > height)) {
            const widthText = ' actual width: ' + tableDescriptor.table.width() + ', expected width: ' + width + ';';
            const heightText = ' actual height: ' + tableDescriptor.table.height() + ', expected height: ' + height + ';';
            const widthProblem = width && tableDescriptor.table.width() > width ? widthText : '';
            const heightProblem = height && tableDescriptor.table.height() > height ? heightText : '';
            const widthDetails = this.validateDetails(tableDescriptor, width, 'width');
            const heightDetails = this.validateDetails(tableDescriptor, height, 'height');
            errorHelper.writeWarning(
                `Incorrect width or height of gridLayout (id: ${tableDescriptor.id});${widthProblem}${heightProblem}${widthDetails}${heightDetails}`
            );
        }
    }

    private validateDetails(tableDescriptor: TableDescriptor, size: number, sizeName: string): string {
        let message = '';
        const propName = sizeName === 'width' ? 'column' : 'row';

        if (size) {
            let hasAsterisk = false;
            _(tableDescriptor[`${propName}s`]).forEach((obj: IGridColumnDescription | RowDescription) => {
                if (!obj.style || !obj.style[sizeName] || obj.style[sizeName] === '*') {
                    hasAsterisk = true;
                }
            });

            if (!hasAsterisk) {
                message += ` There is no ${propName}, which can occupy all empty space.`;
            }

            _(tableDescriptor.cells).forEach(cell => {
                const actualSize = sizeName === 'width' ? cell.actualWidth : cell.actualHeight;
                const cellSize = Math.round(cell.cell[sizeName]());
                if (actualSize && cellSize > actualSize) {
                    message += ' cell ({0}, {1}) {2} ({3}) is bigger than it should be ({4}).'.format(
                        cell.columnIndex,
                        cell.rowIndex,
                        sizeName,
                        cellSize,
                        actualSize
                    );
                    if (cell.content) {
                        const contentSize = Math.round(cell.content[sizeName]());
                        if (contentSize > actualSize) {
                            message += ' Maybe the content {0} is the reason;'.format(sizeName);
                        }
                    }
                }
            });
        }

        return message;
    }

    private getActualSizes(totalSize: number, sizes: SizeDefinition[]): number[] {
        const actualSizes = [];
        const [asterisk, others] = _.partition(Array.from(sizes.entries()), ([i, s]) => s.isAsteriskValue);
        let availableSize = totalSize;
        let asteriskValuesSum = _.sumBy(asterisk, ([i, s]) => s.value);
        for (const [index, size] of [...others, ...asterisk]) {
            if (size.isPercentMaxValue) {
                size.maxValue = (totalSize * size.maxValue) / 100;
            }
            if (size.isPercentMinValue) {
                size.minValue = (totalSize * size.minValue) / 100;
            }
            let calcSize = 0;
            if (size.isAsteriskValue) {
                calcSize = Math.floor((availableSize / asteriskValuesSum) * size.value);
                asteriskValuesSum -= size.value;
            } else if (size.isPercentValue) {
                calcSize = Math.floor((totalSize * size.value) / 100);
            } else {
                calcSize = size.value;
            }

            calcSize = _.clamp(calcSize, size.minValue, size.maxValue);
            availableSize -= calcSize;
            actualSizes[index] = calcSize;
        }
        debug`availableSize: ${availableSize}px`;
        return actualSizes;
    }

    private getSize(tableDescriptor: TableDescriptor, obj: RowDescription | IGridColumnDescription, propertyName: Dimension): SizeDefinition {
        const styleDef = obj.style || {};
        const styleValue = styleDef[propertyName];
        const isEmpty = !styleValue;
        let value = !isEmpty ? parseInt(styleValue) : 0;
        if (isNaN(value)) {
            value = 0;
        }

        if (StringUtils.isEqualsIgnoreCase('auto', styleValue)) {
            value = this.getAutoSize(tableDescriptor, obj, propertyName);
        }

        const maxSize = styleDef[propertyName === 'width' ? 'maxWidth' : 'maxHeight'] || null;
        const minSize = styleDef[propertyName === 'width' ? 'minWidth' : 'minHeight'] || null;
        const maxValue = maxSize ? parseInt(maxSize) : Number.MAX_VALUE;
        const minValue = minSize ? parseInt(minSize) : 0;

        const isPercentValue = _.isString(styleValue) && styleValue.includes('%');
        const isPercentMaxValue = maxSize && _.isString(maxSize) && maxSize.includes('%');
        const isPercentMinValue = minSize && _.isString(minSize) && minSize.includes('%');
        const isAsteriskValue = isEmpty || (_.isString(styleValue) && styleValue.includes('*'));

        return {
            isPercentValue: isPercentValue,
            isPercentMaxValue: isPercentMaxValue,
            isPercentMinValue: isPercentMinValue,
            value: value === 0 && isAsteriskValue ? 1 : value,
            isAsteriskValue: isAsteriskValue,
            maxValue: maxValue,
            minValue: minValue
        };
    }

    public setRowStyle(index: number, style: IGridRowStyle): void {
        const row = this.tableDescriptor.rows[index] as RowDescription;
        row.style = $.extend(row.style, style);
        if (row.style) {
            const css = this.getDefaultStyle(row.style);
            row.row.css(css);
        }
        this.adjust();
    }

    public setColumnStyle(index: number, style: IGridColumnStyle): void {
        const column = this.tableDescriptor.columns[index];
        column.style = style;
        this.adjust();
    }

    public enabled(value?: boolean): boolean {
        _(this.tableDescriptor.cells).forEach(cell => {
            if (cell.table) {
                cell.table.instance.enabled(value);
            } else if (cell.control && cell.control.setState) {
                cell.control.setState(value);
            } else if (cell.control && cell.control.enabled) {
                cell.control.enabled(value);
            } else if (cell.content) {
                cell.content.toggleClass('disabled', !value);
                cell.content.attr('disabled', !value);
            }
        });
        return value;
    }

    public readonly(value: boolean): boolean {
        _(this.tableDescriptor.cells).forEach(cell => {
            if (cell.table) {
                cell.table.instance.readonly(value);
            } else if (cell.control && cell.control.enabled) {
                cell.control.readonly(value);
            } else if (cell.content) {
                cell.content.toggleClass('readonly-control', !value);
                cell.content.attr('readonly-control', !value);
            }
        });
        return value;
    }

    public destroy(): void {
        _(this.tableDescriptor.cells).forEach(cell => {
            if (cell.table && cell.table.instance) {
                cell.table.instance.destroy();
                delete cell.table.instance;
            } else if (cell.control && cell.control.destroy) {
                cell.control.destroy();
            } else if (cell.content) {
                cell.content.remove();
            }
        });
        if (this.tableDescriptor.table) {
            this.tableDescriptor.table.removeData();
        }
        this.tableDescriptor.table = null;
        this.tableDescriptor.instance = null;

        super.destroy();
    }

    public spinnerStart(param?: ISpinnerSetting): void {
        this._root.spinnerStart(param);
    }

    public spinnerStop(): void {
        this._root.spinnerStop();
    }

    public setRowVisibility(rowIndex: number, visible: boolean): void {
        if (visible) {
            this.setRowStyle(rowIndex, { height: 'auto', display: 'table-row' });
        } else {
            this.setRowStyle(rowIndex, { height: '0px', display: 'none' });
        }
    }

    private static getColSpanCellsSize(cell: CellDescription, actualWidths: number[], actualHeights: number[]): ISize {
        let width = actualWidths[cell.columnIndex];
        if (cell.colSpan) {
            width = 0;
            let i = 0;
            while (i < cell.colSpan && cell.columnIndex + i < actualWidths.length) {
                width += actualWidths[cell.columnIndex + i];
                i++;
            }
        }

        return { width: width, height: actualHeights.length > 0 ? actualHeights[cell.rowIndex] : 0 };
    }
}

class DebugGuard {
    @memoize()
    public static isDebugEnabled(): boolean {
        return urlHelper.getUrlParameter('grid-layout-debug') === '1';
    }
}

function debug(message: TemplateStringsArray, ...values: any[]): void {
    if (DebugGuard.isDebugEnabled()) {
        console.log(_.zipWith(message, values, (a, b) => _.defaultTo(a, '') + _.defaultTo(b, '')).join(''));
    }
}
